import { Box } from '@mui/material';
import Email from 'components/form/form-fields/email.component';
import IncButton from 'components/inc-button/inc-button.component';
import InputEmailOrPhone from 'components/input-email-or-phone/input-email-or-phone.component';
import TocAndPrivacy from 'components/toc-and-privacy/toc-and-privacy.component';
import CreateAccountPrompt from 'components/create-account-prompt/create-account-prompt.component';
import {
  HorizontalLine,
  SignupButtonStyled,
  StyledActionLink,
} from 'components/create-account/create-account.styles';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsPasswordlessAuthSmsEnabled } from 'redux/config/config.selectors';
import { passwordlessAuthInitStartAsync } from 'redux/user/user.actions';
import Form from '../form/form.component';
import FormError from 'components/form-error/form-error.component';
import { useState } from 'react';
import { EMAIL_REGEX } from 'util/constants';

const PasswordlessFields = ({ send, isSignUp = false, email }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isSmsEnabled = useSelector(selectIsPasswordlessAuthSmsEnabled);
  const [error, setError] = useState();

  const methods = useForm({ mode: 'onSubmit', reValidateMode: 'onBlur' });
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async ({ identifier, email }) => {
    setError(undefined);
    const trimmed = (identifier ?? email).trim();
    const method = EMAIL_REGEX.test(trimmed) ? 'MAGIC_LINK' : 'SMS_OTP';
    const response = await dispatch(
      passwordlessAuthInitStartAsync({ identifier: trimmed, method }),
    );

    if (response.data) {
      const event =
        method === 'MAGIC_LINK'
          ? 'MAGIC_LINK_CONFIRMATION'
          : 'OTP_CONFIRMATION';
      send(event, {
        destination: response.data.destination,
        isNewUser: response.data.is_new_user,
        method,
        identifier: trimmed,
      });
      return;
    }
    setError(response);
  };

  const postText = isSmsEnabled
    ? t('passwordless.toc.postTextEmailOrSms')
    : t('passwordless.toc.postTextEmail');

  return (
    <FormProvider {...methods}>
      <Box
        component={Form}
        onSubmit={handleSubmit(onSubmit)}
        display="flex"
        flexDirection="column"
        noValidate
      >
        {isSmsEnabled ? (
          <InputEmailOrPhone
            name="identifier"
            label={t('passwordless.identifier.labelWithSms')}
            defaultValue={email}
          />
        ) : (
          <Email errors={errors} register={register} defaultValue={email} />
        )}
        {!!error && <FormError errorMessage={error.errorMessage} />}
        <Box display="grid" rowGap={theme => theme.spacing(1)}>
          <IncButton
            type="submit"
            fullWidth
            disabled={isSubmitting}
            data-testid="PasswordlessFields.Button.Submit"
          >
            {isSignUp
              ? t('passwordless.submitSignUp')
              : t('passwordless.submit')}
          </IncButton>
          <TocAndPrivacy
            description={t('passwordless.toc.description')}
            postText={postText}
          />
        </Box>
        {!isSignUp && (
          <CreateAccountPrompt
            onClick={() => send('REGISTER')}
            data-testid="PasswordlessFields.Link.CreateAccount"
          />
        )}
        {isSignUp && (
          <>
            <HorizontalLine />
            <SignupButtonStyled
              fullWidth
              onClick={() => send('REGISTER')}
              data-testid="PasswordlessFields.Button.CreateWithPassword"
            >
              {t('passwordless.createWithPassword')}
            </SignupButtonStyled>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              mt={1}
            >
              {t('passwordless.alreadyHaveAccount.label')}{' '}
              <StyledActionLink
                onClick={() => send('LOGIN')}
                data-testid="PasswordlessFields.Link.SignIn"
              >
                {t('passwordless.alreadyHaveAccount.action')}
              </StyledActionLink>
            </Box>
          </>
        )}
      </Box>
    </FormProvider>
  );
};

export default PasswordlessFields;
